import { Button, Flex, Text } from '@mantine/core';
import { COLORS, ICON_STYLES, VOUCHER_AMOUNTS } from './constants';
import { VoucherCode } from './VoucherCode';
import { useTranslation } from 'react-i18next';
import { MyIcon } from '@web/common-ui';
import { VoucherType } from '@prisma/client';

function getSurveyVoucherAmount(_surveyIdentifier: string) {
  return VOUCHER_AMOUNTS.DEFAULT;
}

interface VoucherStatesProps {
  onRedeem: () => void;
  vouchers?: Array<{ code: string; type: VoucherType }>;
  surveyIdentifier: string;
}

export const RedeemButton = ({
  onRedeem,
  fullWidth = true,
  amount = VOUCHER_AMOUNTS.DEFAULT,
}: {
  onRedeem: () => void;
  fullWidth?: boolean;
  // varies per outcome for BDM2; every other allowance is a single 10 CHF voucher
  amount?: string;
}) => {
  const { t } = useTranslation('questionnaire');

  return (
    <Button
      onClick={(e) => {
        e.stopPropagation();
        onRedeem();
      }}
      w={fullWidth ? '100%' : undefined}
      rightSection={
        <MyIcon
          name="redeem"
          style={{
            ...ICON_STYLES.small,
            fill: COLORS.WHITE,
          }}
        />
      }
    >
      {amount} {t('VoucherStates.RedeemVoucher')}
    </Button>
  );
};

export const RedeemedVouchers = ({
  vouchers,
  fullWidth = true,
}: Pick<VoucherStatesProps, 'vouchers'> & { fullWidth?: boolean }) => (
  <Flex direction="column" w={fullWidth ? '100%' : undefined}>
    {vouchers?.map((voucher) => (
      <VoucherCode key={voucher.code} code={voucher.code} type={voucher.type} />
    ))}
  </Flex>
);

export const DonatedStatus = () => {
  const { t } = useTranslation('questionnaire');

  return (
    <Flex justify="space-between" w="100%" align="center">
      <Flex gap="4px" align="center">
        <Text size="16px" fw="400" c={COLORS.NEUTRAL}>
          {t('VoucherStates.VoucherDonated')}
        </Text>
        <MyIcon
          name="donate"
          style={{ fill: COLORS.SUCCESS, ...ICON_STYLES.medium }}
        />
      </Flex>
      <Flex gap="4px">
        <Text size="16px" fw="400" c={COLORS.NEUTRAL} lh="18px">
          {VOUCHER_AMOUNTS.DEFAULT}
        </Text>
        <MyIcon
          name="redeem"
          style={{
            ...ICON_STYLES.small,
            fill: COLORS.NEUTRAL,
          }}
        />
      </Flex>
    </Flex>
  );
};

export const TakenStatus = ({
  surveyIdentifier,
}: {
  surveyIdentifier: string;
}) => {
  const { t } = useTranslation('questionnaire');

  const amount = getSurveyVoucherAmount(surveyIdentifier);

  return (
    <Flex justify="space-between" w="100%" align="center">
      <Flex gap="4px" align="center">
        <Text size="16px" fw="400" c={COLORS.NEUTRAL}>
          {t('VoucherStates.VoucherTaken')}
        </Text>
      </Flex>
      <Flex gap="4px">
        <Text size="16px" fw="400" c={COLORS.NEUTRAL} lh="18px">
          {amount}
        </Text>
        <MyIcon
          name="redeem"
          style={{
            ...ICON_STYLES.small,
            fill: COLORS.NEUTRAL,
          }}
        />
      </Flex>
    </Flex>
  );
};

export const ParticipateButton = ({
  surveyIdentifier,
  questionnaireLink,
}: {
  surveyIdentifier: string;
  questionnaireLink: string;
}) => {
  const { t } = useTranslation('questionnaire');

  return (
    <Flex justify="space-between" w="100%" align="center">
      <Flex gap="4px" align="center">
        <Button
          variant="transparent"
          p="0px"
          size="16px"
          onClick={(e) => {
            e.stopPropagation();
            window.open(questionnaireLink, '_blank');
          }}
        >
          {t('VoucherStates.ParticipateNow')} &gt;
        </Button>
      </Flex>
      <Flex gap="4px">
        <Text size="16px" fw="400" c={COLORS.PRIMARY} lh="18px">
          {getSurveyVoucherAmount(surveyIdentifier)}
        </Text>
        <MyIcon
          name="redeem"
          style={{
            ...ICON_STYLES.small,
            fill: COLORS.PRIMARY,
          }}
        />
      </Flex>
    </Flex>
  );
};
